import { BigNumber } from 'ethers'
import { useQuery } from '@tanstack/react-query'

import { getFeeDistributorContract } from '~/contracts/getFeeDistributorContract'
import { getSISContract } from '~/contracts/getSISContract'
import { getVotingEscrowContract } from '~/contracts/getVotingEscrowContract'
import { VotingEscrow } from '~/contracts/types'
import { DepositType } from '~/lib/constants'
import { getWeeklySISDistribution } from '~/lib/getEstimateWeaklyRewards'
import { getVESISAPR } from '~/lib/getVESISAPR'
import { sisLockKeys } from '~/lib/queryKeys'
import { useVESISParams } from '../../../hooks/useVESISParams'

const SECONDS_IN_YEARS = 365 * 60 * 60 * 24

interface Result {
	totalSupply?: BigNumber
	locked?: BigNumber
	estimateYieldPerWeek?: BigNumber
	apr?: number
}

async function getAverageLockTime(
	votingEscrowContract: VotingEscrow
): Promise<number> {
	const depositFilter = votingEscrowContract.filters.Deposit()

	// FIXME deploy transaction https://testnet.bscscan.com/tx/0x7d98e2f217e5a3ec01b1099ed78c53003794d9a176424436324b82d882d8d7d8
	// const startBlock = 20357250
	const latestBlockNumber =
		await votingEscrowContract.provider.getBlockNumber()
	const fromBlock = latestBlockNumber - 50000
	const depositEvents = await votingEscrowContract.queryFilter(
		depositFilter,
		fromBlock,
		latestBlockNumber
	)

	let sum = BigNumber.from(0)
	let count = 0

	const durations = new Map<string, { ts: BigNumber; locktime: BigNumber }>()

	depositEvents.forEach(({ args: { ts, type, locktime, provider } }) => {
		const duration = durations.get(provider)

		switch (type.toNumber()) {
			case DepositType.Deposit:
			case DepositType.CreateLock: {
				if (duration) {
					// New lock from existed provider
					sum = sum.add(duration.locktime.sub(duration.ts))
				}

				durations.set(provider, { ts, locktime })

				count++

				break
			}

			case DepositType.IncreaseLockAmount: {
				if (!duration) {
					return
				}

				durations.set(provider, { ...duration, locktime })

				break
			}
		}
	})

	durations.forEach(({ ts, locktime }) => {
		sum = sum.add(locktime.sub(ts))
	})

	if (!count) {
		return 0
	}

	return sum.div(count).toNumber() / SECONDS_IN_YEARS
}

export function useLockTotalOverview(): Result {
	const { config, provider } = useVESISParams()

	const { data } = useQuery({
		queryKey: sisLockKeys.totalOverview(config.chain.id),
		queryFn: async () => {
			const votingEscrowContract = getVotingEscrowContract(
				config,
				provider
			)
			const feeDistributorContract = getFeeDistributorContract(
				config,
				provider
			)
			const sisContract = getSISContract(config, provider)

			const totalSupply = await votingEscrowContract['totalSupply()']()

			const locked = await sisContract.balanceOf(
				config.votingEscrowAddress
			)

			const estimateYieldPerWeek = await getWeeklySISDistribution(
				config,
				provider,
				feeDistributorContract
			)

			return {
				totalSupply,
				locked,
				estimateYieldPerWeek,
			}
		},
	})

	if (!data) {
		return {}
	}

	const apr = getVESISAPR(data.estimateYieldPerWeek, data.locked)

	return {
		...data,
		apr,
	}
}
